import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ShieldCheck, Smartphone, Sparkles } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "https://api.mogym.ir";

export default function CampaignOtpPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const form =
    location?.state?.form ||
    JSON.parse(localStorage.getItem("campaignWizardForm") || "null");

  const analysis = location?.state?.analysis || null;

  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", ""]);
  const [step, setStep] = useState("mobile");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(120);

  const inputsRef = useRef([]);

  useEffect(() => {
    if (step !== "otp") return;

    const interval = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(interval);
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [step]);

const sendOtp = async () => {
  if (!/^09\d{9}$/.test(mobile)) {
    setError("شماره موبایل معتبر نیست.");
    return;
  }

  try {
    setLoading(true);
    setError("");

    const res = await fetch(`${API_URL}/api/campaign/body-analysis/start`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...buildCampaignStartPayload(form),
        mobileNumber: mobile,
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.message || data?.Message || "ارسال کد ناموفق بود.");
    }

    localStorage.setItem("campaignPlanGuid", data.id);
    localStorage.setItem("campaignMobileNumber", mobile);

    setStep("otp");
    setTimer(120);

    setTimeout(() => {
      inputsRef.current[0]?.focus();
    }, 100);
  } catch (e) {
    setError(e?.message || "ارسال کد ناموفق بود.");
  } finally {
    setLoading(false);
  }
};

const verifyOtp = async () => {
  const code = otp.join("");

  if (code.length !== 5) {
    setError("کد ۵ رقمی را کامل وارد کن.");
    return;
  }

  try {
    setLoading(true);
    setError("");

    const finalPlanGuid = localStorage.getItem("campaignPlanGuid");

    if (!finalPlanGuid) {
      throw new Error("شناسه برنامه پیدا نشد. لطفا دوباره شماره موبایل را وارد کن.");
    }

    const res = await fetch(`${API_URL}/api/campaign/body-analysis/verify-otp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        Id: finalPlanGuid,
        mobileNumber: mobile,
        otpCode: code,
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.message || data?.Message || "کد وارد شده صحیح نیست.");
    }

    localStorage.setItem("campaignToken", data.token);
    localStorage.setItem("campaignPlanGuid", data.id);
    localStorage.setItem("campaignMobileNumber", mobile);

    navigate("/body-analysis/result", {
      state: {
        form,
        analysis: data.analysis,
        mobileNumber: mobile,
        planGuid: data.id,
        token: data.token,
      },
    });
  } catch (e) {
    setError(e?.message || "کد وارد شده صحیح نیست.");
  } finally {
    setLoading(false);
  }
};

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);

    const next = [...otp];
    next[index] = digit;

    setOtp(next);

    if (digit && index < 4) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleBackspace = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  return (
    <main
      dir="rtl"
      className="font-vazir relative min-h-screen overflow-hidden bg-[#030712] text-white"
    >
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(16,185,129,.32),transparent_35%),radial-gradient(circle_at_0%_85%,rgba(37,99,235,.28),transparent_38%),radial-gradient(circle_at_100%_70%,rgba(34,211,238,.16),transparent_30%)]" />

      <div className="fixed inset-0 opacity-[.15] bg-[linear-gradient(rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px)] bg-[size:34px_34px]" />

      <section className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col px-5 py-6">

        <motion.div
          initial={{ opacity: 0, scale: .92 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mt-10 flex justify-center"
        >
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-emerald-400/20 blur-3xl" />

            <div className="relative grid h-28 w-28 place-items-center rounded-full border border-emerald-300/20 bg-black/30 backdrop-blur-xl">
              {step === "mobile" ? (
                <Smartphone
                  size={46}
                  className="text-emerald-300"
                />
              ) : (
                <ShieldCheck
                  size={46}
                  className="text-emerald-300"
                />
              )}
            </div>
          </div>
        </motion.div>

        <h1 className="mt-8 text-center text-2xl font-black">
          {step === "mobile"
            ? "مشاهده پیش‌ نمایش برنامه"
            : "کد تایید رو وارد کن"}
        </h1>

        <p className="mt-4 text-center text-sm leading-7 text-white/60">
          {step === "mobile"
            ? "برای مشاهده پیش‌ نمایش اختصاصی برنامه، شماره موبایل خودتو وارد کن"
            : `کد تایید برای ${mobile} ارسال شد`}
        </p>

        {step === "mobile" && (
          <div className="mt-10">
            <input
              value={mobile}
              onChange={(e) =>
                setMobile(
                  e.target.value.replace(/[^\d]/g, "")
                )
              }
              placeholder="09123456789"
              className="
                h-16
                w-full
                rounded-3xl
                border
                border-white/10
                bg-white/[.05]
                px-5
                text-center
                text-lg
                font-black
                outline-none
                backdrop-blur-xl
              "
            />
          </div>
        )}

        {step === "otp" && (
          <>
<div className="mt-10 flex justify-center gap-2" dir="ltr">
                {otp.map((digit, index) => (
                <input
                dir="ltr"
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  value={digit}
                  onChange={(e) =>
                    handleOtpChange(index, e.target.value)
                  }
                  onKeyDown={(e) =>
                    handleBackspace(index, e)
                  }
                  inputMode="numeric"
                  maxLength={1}
                  className="
                    h-16
                    w-14
                    rounded-2xl
                    border
                    border-white/10
                    bg-white/[.05]
                    text-center
                    text-2xl
                    font-black
                    outline-none
                    backdrop-blur-xl
                  "
                />
              ))}
            </div>

            <div className="mt-6 text-center text-sm text-white/50">
              {timer > 0
                ? `ارسال مجدد تا ${timer} ثانیه دیگر`
                : "ارسال مجدد کد"}
            </div>
          </>
        )}

        {error && (
          <div className="mt-6 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-center text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="mt-auto">


          <button
            disabled={loading}
            onClick={
              step === "mobile"
                ? sendOtp
                : verifyOtp
            }
            className="
              flex
              h-16
              w-full
              items-center
              justify-center
              gap-2
              rounded-3xl
              bg-gradient-to-r
              from-emerald-300
              via-green-400
              to-cyan-400
              font-black
              text-black
              shadow-[0_0_80px_rgba(52,211,153,.5)]
            "
          >
                        <ArrowRight size={20} />

            {loading
              ? "لطفا صبر کن..."
              : step === "mobile"
              ? "دریافت کد تایید"
              : "مشاهده پیش‌ نمایش برنامه"}

          </button>
        </div>
      </section>
    </main>
  );
}

function buildCampaignStartPayload(form) {
  return {
    requestedPlan: Number(form?.requestedPlan ?? 3),
    planType: Number(form?.planType ?? 1),
    price: Number(form?.price ?? 0),

    age: toInt(form?.age),
    height: toInt(form?.height),
    weight: toInt(form?.weight),

    gender: toNullableNumber(form?.gender),
    goal: toNullableNumber(form?.goal),
    fitnessExperience: toNullableNumber(form?.fitnessExperience),

    trainingDaysPerWeek: toNullableNumber(form?.frequency),

    activityLevel: toNullableNumber(form?.activityLevel),
    allergies: toNullableNumber(form?.allergies),
    medicalConditions: toNullableNumber(form?.medicalConditions),
    equipmentAccess: toNullableNumber(form?.equipment),
    bodyFocus: toNullableNumber(form?.bodyFocus),

    waist: toNullableNumber(form?.measurements?.waist),
    arm: toNullableNumber(form?.measurements?.arm),
    thigh: toNullableNumber(form?.measurements?.thigh),
    chest: toNullableNumber(form?.measurements?.chest),
    hip: toNullableNumber(form?.measurements?.hip),

    campaignKey: "body-analysis",
    source: "instagram-landing",
  };
}

function toInt(value) {
  return parseInt(String(value || "").replace(/\D/g, ""), 10) || 0;
}

function toNullableNumber(value) {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}